// Senderbereich: Moderatoren, Webhooks, Statistik, Öffentliche Seite (mit Wünsche-Inbox).

import { useState } from "react";
import { api, fmtTime, toast, useLoad } from "../../api";
import { Empty, Field, Modal, Switch, Tip, useMe } from "../../ui";

const DAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const toMin = (s: string) => { const [h, m] = s.split(":").map(Number); return (h || 0) * 60 + (m || 0); };

// ---------------------------------------------------------------- hosts

export function Hosts() {
  const me = useMe();
  const can = ["admin", "programm"].includes(me?.role);
  const { data: hosts, reload } = useLoad<any[]>("/api/hosts");
  const { data: users } = useLoad<any[]>(me?.role === "admin" ? "/api/users" : null);
  const [edit, setEdit] = useState<any | null>(null);
  const save = async () => {
    try {
      const b = { ...edit };
      if (edit.id) await api(`/api/hosts/${edit.id}`, { method: "PUT", body: b });
      else await api("/api/hosts", { body: b });
      setEdit(null);
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const photo = async (id: string, f: File) => {
    try {
      await api(`/api/hosts/${id}/photo`, { raw: f, type: "application/x-radiocast-image" });
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <div className="stack">
      <div className="row"><span className="spacer" />{can && <button className="btn primary" onClick={() => setEdit({ name: "", slogan: "", bio: "", links: {}, live_windows: [], public: true })}>+ Moderator:in</button>}</div>
      <Tip>Moderator:innen erscheinen auf der öffentlichen Seite und im Programm. Mit einem Konto verknüpft sehen sie in „Meine Sendungsvorbereitung“ ihre Sendungen und offenen Voicetracks. Die Live-Zeiten zeigen, wer wann live senden darf.</Tip>
      {!hosts?.length ? <Empty>Noch keine Moderator:innen.</Empty> : (
        <div className="grid g3">
          {hosts.map((h) => (
            <div key={h.id} className="card">
              <div className="row">
                {h.photo ? <img src={`/api/public/host-photo/${h.id}?v=${Date.now() % 1e5}`} alt="" style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover" }} /> : <div className="cover" style={{ width: 56, height: 56, borderRadius: "50%" }}>{h.name.slice(0, 1)}</div>}
                <div><b>{h.name}</b><div className="small muted">{h.slogan}</div>{h.login && <span className="badge info">Konto: {h.login}</span>}</div>
              </div>
              <p className="small" style={{ minHeight: 30 }}>{h.bio}</p>
              <div className="small muted">{(h.live_windows ?? []).map((w: any, i: number) => <span key={i} className="badge" style={{ marginRight: 4 }}>live {(w.days ?? []).map((d: number) => DAYS[d]).join(" ")} {hhmm(w.from_min)}–{hhmm(w.to_min)}</span>)}</div>
              {can && (
                <div className="row" style={{ marginTop: 8 }}>
                  <label className="btn small">Foto …<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) photo(h.id, f); }} /></label>
                  <span className="spacer" />
                  <button className="btn small" onClick={() => setEdit(h)}>Bearbeiten</button>
                  <button className="btn small danger" onClick={async () => { await api(`/api/hosts/${h.id}`, { method: "DELETE" }); reload(); }}>✕</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {edit && (
        <Modal title={edit.id ? edit.name : "Neue Moderator:in"} onClose={() => setEdit(null)}>
          <div className="form">
            <Field label="Name"><input type="text" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Slogan"><input type="text" value={edit.slogan} onChange={(e) => setEdit({ ...edit, slogan: e.target.value })} /></Field>
            <Field label="Bio" full><textarea rows={3} value={edit.bio} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} /></Field>
            {["instagram", "facebook", "tiktok", "website"].map((k) => (
              <Field key={k} label={k[0].toUpperCase() + k.slice(1)}><input type="url" value={edit.links?.[k] ?? ""} onChange={(e) => setEdit({ ...edit, links: { ...edit.links, [k]: e.target.value } })} /></Field>
            ))}
            {users && <Field label="Konto"><select value={edit.user_id ?? ""} onChange={(e) => setEdit({ ...edit, user_id: e.target.value })}><option value="">– keins –</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.login})</option>)}</select></Field>}
            <Field label="Öffentlich zeigen"><Switch checked={edit.public !== false} onChange={(x) => setEdit({ ...edit, public: x })} /></Field>
            <div className="field full">
              <span>Darf live senden</span>
              {(edit.live_windows ?? []).map((w: any, i: number) => (
                <div key={i} className="row wrap" style={{ marginBottom: 4 }}>
                  {DAYS.map((d, j) => (
                    <label key={d} className="badge" style={{ cursor: "pointer", background: (w.days ?? []).includes(j) ? "var(--accent-2)" : undefined }}>
                      <input type="checkbox" checked={(w.days ?? []).includes(j)} onChange={() => setEdit({ ...edit, live_windows: edit.live_windows.map((x: any, k: number) => k === i ? { ...x, days: (x.days ?? []).includes(j) ? x.days.filter((y: number) => y !== j) : [...(x.days ?? []), j] } : x) })} /> {d}
                    </label>
                  ))}
                  <input type="time" value={hhmm(w.from_min)} style={{ width: 110 }} onChange={(e) => setEdit({ ...edit, live_windows: edit.live_windows.map((x: any, k: number) => k === i ? { ...x, from_min: toMin(e.target.value) } : x) })} />
                  <input type="time" value={hhmm(w.to_min)} style={{ width: 110 }} onChange={(e) => setEdit({ ...edit, live_windows: edit.live_windows.map((x: any, k: number) => k === i ? { ...x, to_min: toMin(e.target.value) } : x) })} />
                  <button className="iconbtn" onClick={() => setEdit({ ...edit, live_windows: edit.live_windows.filter((_: any, k: number) => k !== i) })}>✕</button>
                </div>
              ))}
              <button className="btn small" style={{ alignSelf: "flex-start" }} onClick={() => setEdit({ ...edit, live_windows: [...(edit.live_windows ?? []), { days: [], from_min: 1080, to_min: 1200 }] })}>+ Zeitfenster</button>
            </div>
          </div>
          <div className="row" style={{ marginTop: 12 }}><span className="spacer" /><button className="btn primary" onClick={save}>Speichern</button></div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- webhooks

const EVENTS: [string, string][] = [["track_change", "Titelwechsel"], ["live_start", "Live-Start (Übernahme)"], ["live_end", "Live-Ende"], ["silence", "Stille"], ["output_down", "Ausgang weg"], ["request", "Neuer Hörerwunsch"]];

export function Webhooks() {
  const me = useMe();
  const can = me?.role === "admin";
  const { data: hooks, reload } = useLoad<any[]>("/api/webhooks");
  const [edit, setEdit] = useState<any | null>(null);
  const [log, setLog] = useState<any | null>(null);
  const { data: deliveries, reload: reloadLog } = useLoad<any[]>(log ? `/api/webhooks/${log.id}/deliveries` : null, [log?.id]);
  return (
    <div className="stack">
      <div className="row"><span className="spacer" />{can && <button className="btn primary" onClick={() => setEdit({ name: "", url: "https://", events: ["track_change"] })}>+ Webhook</button>}</div>
      <Tip>Radiocast schickt bei den gewählten Ereignissen ein JSON per POST an die Adresse. Die Kopfzeile „X-Radiocast-Signature“ (HMAC-SHA256 mit dem Geheimnis) beweist die Herkunft. Fehlgeschlagene Zustellungen werden nach 30 s und 5 min wiederholt.</Tip>
      {!hooks?.length ? <Empty>Keine Webhooks.</Empty> : (
        <div className="card" style={{ padding: 0 }}>
          <table className="list">
            <tbody>
              {hooks.map((h) => (
                <tr key={h.id} style={{ opacity: h.enabled ? 1 : 0.5 }}>
                  <td><b>{h.name}</b><div className="mono small muted">{h.url}</div></td>
                  <td className="small">{(h.events ?? []).map((e: string) => EVENTS.find(([k]) => k === e)?.[1] ?? e).join(", ")}</td>
                  <td className="small">{h.last ? <span className={`badge ${h.last.error ? "bad" : "ok"}`}>{h.last.error ?? `HTTP ${h.last.status}`}</span> : <span className="muted">noch nichts</span>}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn small" onClick={() => setLog(h)}>Protokoll</button>{" "}
                    {can && <button className="btn small" onClick={async () => { const r = await api(`/api/webhooks/${h.id}/test`, { method: "POST" }); toast(r?.error ? `Test: ${r.error}` : `Test: HTTP ${r?.status}`, !!r?.error); reload(); }}>Testen</button>}{" "}
                    {can && <Switch checked={h.enabled} onChange={async (x) => { await api(`/api/webhooks/${h.id}`, { method: "PUT", body: { enabled: x } }); reload(); }} />}{" "}
                    {can && <button className="btn small danger" onClick={async () => { await api(`/api/webhooks/${h.id}`, { method: "DELETE" }); reload(); }}>✕</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && (
        <Modal title="Neuer Webhook" onClose={() => setEdit(null)}>
          <div className="form">
            <Field label="Name"><input type="text" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Adresse"><input type="url" value={edit.url} onChange={(e) => setEdit({ ...edit, url: e.target.value })} /></Field>
            <div className="field full"><span>Ereignisse</span><div className="row wrap">{EVENTS.map(([k, l]) => (
              <label key={k} className="badge" style={{ cursor: "pointer", background: edit.events.includes(k) ? "var(--accent-2)" : undefined }}>
                <input type="checkbox" checked={edit.events.includes(k)} onChange={() => setEdit({ ...edit, events: edit.events.includes(k) ? edit.events.filter((x: string) => x !== k) : [...edit.events, k] })} /> {l}
              </label>))}</div></div>
          </div>
          <div className="row" style={{ marginTop: 12 }}><span className="spacer" /><button className="btn primary" onClick={async () => {
            try { const r = await api("/api/webhooks", { body: edit }); setEdit(null); reload(); window.prompt("Geheimnis für die Signaturprüfung (wird nur jetzt angezeigt):", r.secret); } catch (e: any) { toast(e.message, true); }
          }}>Anlegen</button></div>
        </Modal>
      )}
      {log && (
        <Modal title={`Zustellungen: ${log.name}`} onClose={() => setLog(null)}>
          {!deliveries?.length ? <Empty>Noch keine.</Empty> : (
            <table className="list"><tbody>{deliveries.map((d) => (
              <tr key={d.id}><td className="small muted">{fmtTime(d.at)}</td><td>{d.event}</td><td><span className={`badge ${d.error ? "bad" : "ok"}`}>{d.error ?? `HTTP ${d.status}`}</span></td><td className="small muted">Versuch {d.attempts}</td>
                <td><button className="btn small" onClick={async () => { await api(`/api/webhooks/${log.id}/deliveries/${d.id}/resend`, { method: "POST" }); reloadLog(); }}>Erneut senden</button></td></tr>))}</tbody></table>
          )}
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- statistics

export function Stats() {
  const [days, setDays] = useState(7);
  const { data } = useLoad<any>(`/api/stats?days=${days}`, [days]);
  const { data: now } = useLoad<any>("/api/stats/now");
  const maxCurve = Math.max(1, ...((data?.curve ?? []).map((c: any) => Number(c.peak))));
  const totalHours = (data?.daily ?? []).reduce((a: number, d: any) => a + Number(d.listener_hours), 0);
  const peak = Math.max(0, ...((data?.daily ?? []).map((d: any) => d.peak)));
  return (
    <div className="stack">
      <div className="row"><select style={{ width: 160 }} value={days} onChange={(e) => setDays(Number(e.target.value))}>{[1, 7, 30, 90, 365].map((d) => <option key={d} value={d}>letzte {d} {d === 1 ? "Tag" : "Tage"}</option>)}</select></div>
      <div className="grid g4">
        <div className="card kpi"><span className="v">{now?.unique_10min ?? now?.listeners ?? "–"}</span><span className="l">{now?.unique_10min != null ? "Hörer jetzt (Geräte, 10 min)" : "Hörer jetzt"}</span></div>
        <div className="card kpi"><span className="v">{peak}</span><span className="l">Spitze im Zeitraum</span></div>
        <div className="card kpi"><span className="v">{totalHours.toLocaleString("de-DE", { maximumFractionDigits: 0 })}</span><span className="l">Hörerstunden</span></div>
        <div className="card kpi"><span className="v">{data?.daily?.length ? Math.round(data.daily.reduce((a: number, d: any) => a + (d.avg_session_s ?? 0), 0) / data.daily.length / 60) || "–" : "–"}</span><span className="l">Ø Sitzung (min)</span></div>
      </div>
      <Tip>Hörerzahlen kommen minütlich aus dem Status deiner Icecast-/Shoutcast-Server. Eindeutige Geräte und Sitzungsdauer gibt es, wenn beim Ausgang ein Lesezugang zur Hörerliste hinterlegt ist (Einstellungen › Ausgänge). Länder kommen aus einer lokalen GeoIP-Datenbank – keine Hörer-IP verlässt den Server.</Tip>
      <div className="card">
        <h2>Tageskurve (Ø je Stunde)</h2>
        <div className="row" style={{ alignItems: "flex-end", height: 160, gap: 3 }}>
          {Array.from({ length: 24 }, (_, h) => {
            const c = (data?.curve ?? []).find((x: any) => x.hour === h);
            return <div key={h} title={`${h} Uhr: Ø ${c?.avg ?? 0}, Spitze ${c?.peak ?? 0}`} style={{ flex: 1, height: `${((c?.peak ?? 0) / maxCurve) * 100}%`, background: "var(--accent)", opacity: 0.85, borderRadius: "3px 3px 0 0", minHeight: 2 }} />;
          })}
        </div>
        <div className="row small muted" style={{ justifyContent: "space-between" }}><span>0</span><span>6</span><span>12</span><span>18</span><span>23 Uhr</span></div>
      </div>
      <div className="grid g2">
        <div className="card">
          <h2>Tage</h2>
          <table className="list"><thead><tr><th>Tag</th><th>Spitze</th><th>Ø</th><th>Hörerstunden</th></tr></thead>
            <tbody>{(data?.daily ?? []).map((d: any) => <tr key={d.day}><td>{new Date(d.day).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}</td><td>{d.peak}</td><td>{d.avg}</td><td>{d.listener_hours}</td></tr>)}</tbody></table>
        </div>
        <div className="card">
          <h2>Länder</h2>
          {!data?.countries ? <p className="small muted">{data?.geo_note}</p> : !data.countries.length ? <Empty>Noch keine Daten.</Empty> : (() => {
            const total = data.countries.reduce((a: number, c: any) => a + c.minutes, 0) || 1;
            const name = (cc: string) => { try { return cc === "??" ? "unbekannt" : new Intl.DisplayNames(["de"], { type: "region" }).of(cc) ?? cc; } catch { return cc; } };
            return (
              <table className="list"><tbody>{data.countries.map((c: any) => (
                <tr key={c.country}><td>{name(c.country)}</td>
                  <td style={{ width: "50%" }}><div style={{ height: 8, borderRadius: 4, background: "var(--accent)", width: `${(c.minutes / total) * 100}%` }} /></td>
                  <td className="small">{Math.round((c.minutes / total) * 100)} %</td></tr>))}</tbody></table>
            );
          })()}
          {data?.countries && <div className="small muted" style={{ marginTop: 4 }}>{data.geo_note}</div>}
        </div>
        <div className="card">
          <h2>Meistgespielt (mit Ø Hörern)</h2>
          <table className="list"><tbody>{(data?.top ?? []).map((t: any, i: number) => <tr key={i}><td>{t.artist} – {t.title}</td><td className="small">{t.plays}×</td><td className="small muted">Ø {t.avg_listeners ?? "–"}</td></tr>)}</tbody></table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- public page + inbox

const BLOCKS: [string, string, any][] = [
  ["hero", "Kopfbereich (Titel, Bild)", { title: "", subtitle: "", image: "" }],
  ["player", "Player / Jetzt läuft", {}],
  ["history", "Zuletzt gespielt", { heading: "Zuletzt gespielt", count: 10 }],
  ["schedule", "Programm", {}],
  ["requests", "Wunsch-Formular", {}],
  ["greetings", "Grüße der Hörer", { heading: "Grüße" }],
  ["hosts", "Moderator:innen", { heading: "Unser Team" }],
  ["text", "Text", { heading: "", body: "" }],
  ["image", "Bild", { src: "", alt: "", link: "" }],
  ["buttons", "Knöpfe / Links", { items: "Instagram | https://instagram.com/\nWebsite | https://" }],
  ["html", "Eigenes HTML (abgeschottet)", { code: "<p style=\"color:#fff\">Hallo</p>", height: 200 }],
  ["spacer", "Abstand", { height: 24 }],
  ["divider", "Trennlinie", {}],
  ["sisters", "Schwestersender", { heading: "Auch hören" }],
];
const TEMPLATES: Record<string, string[]> = {
  Klassisch: ["hero", "player", "history", "schedule", "requests", "greetings"],
  Minimal: ["hero", "player"],
  Programmseite: ["hero", "player", "schedule", "hosts", "text"],
  Community: ["hero", "player", "requests", "greetings", "hosts", "buttons"],
};
const FIELD_LABELS: Record<string, string> = { title: "Titel", subtitle: "Untertitel", image: "Bild-Adresse (https://…)", heading: "Überschrift", count: "Anzahl", body: "Text (Leerzeile = neuer Absatz)", src: "Bild-Adresse (https://…)", alt: "Beschreibung", link: "Link beim Klick", items: "Je Zeile: Beschriftung | https://…", code: "HTML/CSS/JS (läuft abgeschottet)", height: "Höhe (px)", align: "Ausrichtung" };

function PageBuilder({ p, slug, onSaved }: { p: any; slug: string; onSaved: () => void }) {
  const me = useMe();
  const can = me?.role === "admin";
  const [blocks, setBlocks] = useState<any[]>(p.layout?.blocks ?? []);
  const [open, setOpen] = useState<string | null>(null);
  const [css, setCss] = useState(p.custom_css ?? "");
  const [font, setFont] = useState(p.font ?? "system");
  const [tick, setTick] = useState(0);
  const [drag, setDrag] = useState<number | null>(null);
  const nid = () => Math.random().toString(36).slice(2, 9);
  const add = (type: string) => { const def = BLOCKS.find(([k]) => k === type)!; const b = { id: nid(), type, props: { ...def[2] } }; setBlocks([...blocks, b]); setOpen(b.id); };
  const move = (i: number, d: number) => { const n = [...blocks]; const [x] = n.splice(i, 1); n.splice(Math.max(0, Math.min(n.length, i + d)), 0, x); setBlocks(n); };
  const save = async () => {
    try {
      await api("/api/public-page", { method: "PUT", body: { layout: blocks.length ? { blocks } : null, custom_css: css, font } });
      toast("Seite gespeichert");
      setTick((t) => t + 1);
      onSaved();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <div className="card">
      <div className="row wrap" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Seitenbau</h2><span className="spacer" />
        <span className="small muted">Vorlage:</span>
        {Object.entries(TEMPLATES).map(([n, types]) => <button key={n} className="btn small" disabled={!can} onClick={() => { if (!blocks.length || window.confirm("Aktuelle Blöcke durch die Vorlage ersetzen?")) setBlocks(types.map((t) => ({ id: nid(), type: t, props: { ...BLOCKS.find(([k]) => k === t)![2] } }))); }}>{n}</button>)}
        <button className="btn small" disabled={!can || !blocks.length} onClick={() => window.confirm("Seitenbau ausschalten? Die Seite zeigt dann wieder die einfache Ansicht mit den Bereichen oben.") && setBlocks([])}>Einfache Ansicht</button>
      </div>
      <div className="grid g2" style={{ alignItems: "start" }}>
        <div>
          {!blocks.length && <Empty>Ohne Blöcke zeigt die Seite die einfache Ansicht (Bereiche oben). Vorlage wählen oder Block hinzufügen.</Empty>}
          {blocks.map((b, i) => {
            const def = BLOCKS.find(([k]) => k === b.type);
            return (
              <div key={b.id} draggable={can} onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag != null && drag !== i) move(drag, i - drag); setDrag(null); }}
                style={{ border: "1px solid var(--line)", borderRadius: 8, padding: "6px 8px", marginBottom: 6, background: open === b.id ? "#fbfaf6" : undefined }}>
                <div className="row">
                  <span style={{ cursor: "grab" }} title="Ziehen zum Umsortieren">⠿</span>
                  <b style={{ flex: 1, cursor: "pointer" }} onClick={() => setOpen(open === b.id ? null : b.id)}>{def?.[1] ?? b.type}</b>
                  <button className="iconbtn" disabled={!can || i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button className="iconbtn" disabled={!can || i === blocks.length - 1} onClick={() => move(i, 1)}>↓</button>
                  <button className="iconbtn" disabled={!can} onClick={() => setBlocks(blocks.filter((x) => x.id !== b.id))}>✕</button>
                </div>
                {open === b.id && Object.keys(def?.[2] ?? {}).length > 0 && (
                  <div className="form" style={{ marginTop: 6 }}>
                    {Object.keys(def![2]).map((k) => {
                      const v = b.props?.[k] ?? "";
                      const set = (x: any) => setBlocks(blocks.map((y) => (y.id === b.id ? { ...y, props: { ...y.props, [k]: x } } : y)));
                      const long = ["body", "items", "code"].includes(k);
                      return <Field key={k} label={FIELD_LABELS[k] ?? k} full={long}>{long ? <textarea rows={k === "code" ? 6 : 4} className={k === "code" ? "mono" : undefined} value={v} onChange={(e) => set(e.target.value)} /> : <input type={["count", "height"].includes(k) ? "number" : "text"} value={v} onChange={(e) => set(e.target.value)} />}</Field>;
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {can && (
            <div className="row wrap" style={{ marginTop: 8 }}>
              <select value="" onChange={(e) => e.target.value && add(e.target.value)} style={{ width: 220 }}><option value="">+ Block hinzufügen …</option>{BLOCKS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
              <select value={font} onChange={(e) => setFont(e.target.value)} style={{ width: 160 }}><option value="system">Schrift: Standard</option><option value="serif">Serif</option><option value="rounded">Rund</option><option value="condensed">Schmal</option><option value="mono">Schreibmaschine</option></select>
            </div>
          )}
          <Field label="Eigenes CSS (optional)" full hint="Blöcke haben die Klassen .rc-block und .rc-<typ>, die Seite .rc-public."><textarea rows={4} className="mono" disabled={!can} value={css} onChange={(e) => setCss(e.target.value)} /></Field>
          {can && <div className="row" style={{ marginTop: 8 }}><span className="spacer" /><button className="btn primary" onClick={save}>Speichern</button></div>}
        </div>
        <div>
          <div className="small muted" style={{ marginBottom: 4 }}>Vorschau (nach dem Speichern, auch wenn die Seite noch nicht veröffentlicht ist)</div>
          <iframe key={tick} title="Vorschau" src={`/player/${slug}?preview=${tick + 1}`} style={{ width: "100%", height: 620, border: "1px solid var(--line)", borderRadius: 10 }} />
        </div>
      </div>
    </div>
  );
}

export function PublicSettings() {
  const me = useMe();
  const can = me?.role === "admin";
  const { data: p, reload } = useLoad<any>("/api/public-page");
  const { data: outs } = useLoad<any[]>("/api/outputs");
  const { data: st } = useLoad<any>("/api/station");
  const [status, setStatus] = useState("new");
  const { data: inbox, reload: reloadInbox } = useLoad<any[]>(`/api/requests?status=${status}`, [status]);
  if (!p) return null;
  const put = async (b: any) => {
    try {
      await api("/api/public-page", { method: "PUT", body: b });
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const url = st ? `${window.location.origin}/player/${st.slug}` : "";
  const SECT: [string, string][] = [["live", "Live / Jetzt läuft"], ["history", "Verlauf"], ["schedule", "Programm"], ["requests", "Wünsche & Grüße"], ["hosts", "Moderator:innen"], ["about", "Über uns"]];
  return (
    <div className="stack">
      <div className="card">
        <div className="row wrap" style={{ marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Öffentliche Seite & Player</h2><span className="spacer" />
          <Switch checked={p.enabled} onChange={(x) => can && put({ enabled: x })} label={p.enabled ? "veröffentlicht" : "nicht veröffentlicht"} />
          {p.enabled && <a className="btn small" href={url} target="_blank" rel="noreferrer">Öffnen ↗</a>}
        </div>
        <div className="form">
          <Field label="Titel"><input type="text" disabled={!can} defaultValue={p.title} onBlur={(e) => put({ title: e.target.value })} /></Field>
          <Field label="Untertitel"><input type="text" disabled={!can} defaultValue={p.tagline} onBlur={(e) => put({ tagline: e.target.value })} /></Field>
          <Field label="Akzentfarbe"><input type="color" disabled={!can} value={p.accent} onChange={(e) => put({ accent: e.target.value })} /></Field>
          <Field label="Hintergrund"><input type="color" disabled={!can} value={p.background} onChange={(e) => put({ background: e.target.value })} /></Field>
          <Field label="Jetzt läuft zeigt"><select disabled={!can} value={p.now_playing} onChange={(e) => put({ now_playing: e.target.value })}><option value="songs">nur Songs</option><option value="all">alles</option><option value="none">nichts</option></select></Field>
          <Field label="Programmansicht"><select disabled={!can} value={p.schedule_view} onChange={(e) => put({ schedule_view: e.target.value })}><option value="shows">Sendungen</option><option value="format">Format</option><option value="both">beides</option><option value="off">aus</option></select></Field>
          <Field label="Text bei Live ohne Titel"><input type="text" disabled={!can} defaultValue={p.live_text} onBlur={(e) => put({ live_text: e.target.value })} /></Field>
          <Field label="Stream für den Player"><select disabled={!can} value={p.output_id ?? ""} onChange={(e) => put({ output_id: e.target.value })}><option value="">automatisch</option>{(outs ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></Field>
          <Field label="Hörerwünsche & Grüße"><Switch checked={p.requests_enabled} onChange={(x) => can && put({ requests_enabled: x })} /></Field>
          <Field label="Sprachnachrichten"><Switch checked={p.voice_enabled} onChange={(x) => can && put({ voice_enabled: x })} /></Field>
          <div className="field full"><span>Bereiche</span><div className="row wrap">{SECT.map(([k, l]) => (
            <label key={k} className="badge" style={{ cursor: "pointer", background: p.sections.includes(k) ? "var(--accent-2)" : undefined }}>
              <input type="checkbox" disabled={!can} checked={p.sections.includes(k)} onChange={() => put({ sections: p.sections.includes(k) ? p.sections.filter((x: string) => x !== k) : [...p.sections, k] })} /> {l}
            </label>))}</div></div>
          <Field label="Über uns" full><textarea rows={3} disabled={!can} defaultValue={p.about} onBlur={(e) => put({ about: e.target.value })} /></Field>
        </div>
      </div>
      {st && <PageBuilder p={p} slug={st.slug} onSaved={reload} />}
      <div className="card">
        <h2>Einbetten (Widgets)</h2>
        <p className="small muted" style={{ marginTop: 0 }}>Für deine Website: den Code kopieren und einfügen.</p>
        {[["Player", "player", 380], ["Leiste", "bar", 72], ["Pille", "pill", 48], ["Verlauf", "history", 360], ["Programm", "schedule", 360], ["Wünsche", "requests", 420]].map(([l, w, h]) => (
          <div key={w as string} className="row" style={{ marginBottom: 4 }}>
            <span style={{ width: 90 }}>{l}</span>
            <input type="text" readOnly value={`<iframe src="${url}?widget=${w}" style="border:0;width:100%;height:${h}px" allow="autoplay"></iframe>`} onFocus={(e) => e.target.select()} />
          </div>
        ))}
      </div>
      <div className="card">
        <div className="row" style={{ marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Wünsche, Grüße, Sprachnachrichten</h2><span className="spacer" />
          <select style={{ width: 160 }} value={status} onChange={(e) => setStatus(e.target.value)}><option value="new">neu</option><option value="approved">freigegeben</option><option value="done">erledigt</option><option value="rejected">abgelehnt</option><option value="">alle</option></select>
        </div>
        {!inbox?.length ? <Empty>Nichts da.</Empty> : inbox.map((r) => (
          <div key={r.id} className="row wrap" style={{ padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
            <span className="badge">{r.kind === "request" ? "Wunsch" : r.kind === "greeting" ? "Gruß" : "Sprachnachricht"}</span>
            <div style={{ flex: 1, minWidth: 200 }}>
              <b>{r.name || "anonym"}</b> <span className="small muted">{fmtTime(r.created_at)}</span>
              {r.song && <div>🎵 {r.song}</div>}
              {r.message && <div className="small">{r.message}</div>}
              {r.audio && <audio controls preload="none" src={`/api/requests/${r.id}/audio`} style={{ height: 32 }} />}
            </div>
            {r.status !== "approved" && <button className="btn small" onClick={async () => { await api(`/api/requests/${r.id}`, { method: "PUT", body: { status: "approved" } }); reloadInbox(); }}>Freigeben</button>}
            {r.status !== "done" && <button className="btn small" onClick={async () => { await api(`/api/requests/${r.id}`, { method: "PUT", body: { status: "done" } }); reloadInbox(); }}>Erledigt</button>}
            {r.status !== "rejected" && <button className="btn small danger" onClick={async () => { await api(`/api/requests/${r.id}`, { method: "PUT", body: { status: "rejected" } }); reloadInbox(); }}>Ablehnen</button>}
          </div>
        ))}
        <div className="small muted" style={{ marginTop: 6 }}>Freigegebene Grüße erscheinen auf der öffentlichen Seite.</div>
      </div>
    </div>
  );
}
